/**
 * InfoTrack-style submission form dialog for post-completion workflows.
 *
 * Each statutory workflow (SDLT5, AP1, MR01) is rendered as a single
 * scrollable accordion of panels — mirroring InfoTrack's "single page,
 * 70-90% pre-populated, panel-grouped" UX.
 *
 * Per kind we delegate the actual schema, defaults, validation and
 * panel components to:
 *    /post-completion/forms/SdltForm.jsx
 *    /post-completion/forms/Ap1Form.jsx
 *    /post-completion/forms/Mr01Form.jsx
 *
 * The lighter "redemption" + "leasehold_notice" workflows stay as a
 * flat 5-field form because they're internal tracking, not statutory
 * forms — they live in LIGHT_SCHEMAS below.
 *
 * Behaviour:
 *   - Defaults applied on open from caseData (auto-fill).
 *   - Debounced auto-save to `item.metadata` every 1.2s of inactivity
 *     so partial completion is never lost (mirrors InfoTrack draft).
 *   - "Save draft" forces an immediate write.
 *   - "Submit" runs full validation, then calls the existing /submit
 *     endpoint with the whole metadata payload.
 *   - Per-panel "Complete" / "x / y" / "Needs attention" badge so the
 *     solicitor knows what's outstanding at a glance.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { PaperPlaneTilt, FloppyDisk, CheckCircle, Warning, Eye, Copy } from "@phosphor-icons/react";

import { PanelStatusBadge } from "./post-completion/forms/FormPrimitives";
import { sdltPanels, defaultsSdlt, validateSdlt } from "./post-completion/forms/SdltForm";
import { ap1Panels, defaultsAp1, validateAp1 } from "./post-completion/forms/Ap1Form";
import { mr01Panels, defaultsMr01, validateMr01 } from "./post-completion/forms/Mr01Form";

// ----- Light schemas for non-statutory workflows -----------------
const LIGHT_SCHEMAS = {
  redemption: {
    title: "Mortgage Redemption — confirmation details",
    description:
      "Track the lender's redemption confirmation. Use this when you've sent the redemption funds and want to chase the final statement.",
    submitLabel: "Record redemption",
    defaults: (caseData, item) => ({
      lender_name: item?.metadata?.lender_name || caseData?.lender_name || "",
      account_reference: item?.metadata?.account_reference || "",
      redemption_amount: item?.metadata?.redemption_amount || "",
      redemption_date: item?.metadata?.redemption_date || caseData?.target_completion_date || todayIso(),
      contact_email: item?.metadata?.contact_email || "",
    }),
    fields: [
      { key: "lender_name", label: "Lender", type: "text", required: true },
      { key: "account_reference", label: "Mortgage account reference", type: "text", required: true },
      { key: "redemption_amount", label: "Final redemption amount (£)", type: "number", required: true, step: "0.01" },
      { key: "redemption_date", label: "Date funds sent", type: "date", required: true },
      { key: "contact_email", label: "Lender contact email", type: "email" },
    ],
  },
  leasehold_notice: {
    title: "Notice of Transfer / Charge — leasehold",
    description:
      "Record the notice you're serving on the freeholder or managing agent. Most leases require the notice within 30 days.",
    submitLabel: "Record notice",
    defaults: (caseData, item) => ({
      freeholder_name: item?.metadata?.freeholder_name || "",
      freeholder_email: item?.metadata?.freeholder_email || "",
      managing_agent: item?.metadata?.managing_agent || "",
      notice_type: item?.metadata?.notice_type || "Transfer + Charge",
      fee_paid: item?.metadata?.fee_paid || "",
      notice_date: item?.metadata?.notice_date || todayIso(),
    }),
    fields: [
      { key: "freeholder_name", label: "Freeholder name", type: "text", required: true },
      { key: "freeholder_email", label: "Freeholder email", type: "email" },
      { key: "managing_agent", label: "Managing agent (if applicable)", type: "text" },
      {
        key: "notice_type",
        label: "Notice type",
        type: "select",
        options: [
          { value: "Transfer", label: "Notice of Transfer" },
          { value: "Charge", label: "Notice of Charge" },
          { value: "Transfer + Charge", label: "Notice of Transfer + Charge" },
        ],
      },
      { key: "fee_paid", label: "Notice fee paid (£)", type: "number", step: "0.01" },
      { key: "notice_date", label: "Notice served on", type: "date", required: true },
    ],
  },
};

// ----- Full-form schema lookup ----------------------------------
const FULL_SCHEMAS = {
  sdlt: {
    title: "SDLT5 Return — HMRC submission",
    description: "Fill in every panel of the SDLT1 — InfoTrack-style. Auto-saved as you go.",
    submitLabel: "Submit to HMRC",
    getPanels: sdltPanels,
    getDefaults: defaultsSdlt,
    validate: validateSdlt,
  },
  ap1: {
    title: "AP1 — HMLR Application",
    description: "Land Registry application — title numbers, applicants, applications in priority order, fee, enclosures.",
    submitLabel: "Submit to HMLR",
    getPanels: ap1Panels,
    getDefaults: defaultsAp1,
    validate: validateAp1,
  },
  mr01: {
    title: "MR01 — Companies House Charge Registration",
    description: "Particulars of charge. Filed directly with Companies House via the XML Gateway — attach the certified copy of the signed deed before submitting.",
    submitLabel: "File with Companies House",
    getPanels: mr01Panels,
    getDefaults: defaultsMr01,
    validate: validateMr01,
  },
};

function todayIso() { return new Date().toISOString().slice(0, 10); }

// ----- Light dialog body (redemption / leasehold notice) ---------

function LightForm({ schema, values, setValues, errors }) {
  const handleChange = (key, v) => setValues((s) => ({ ...s, [key]: v }));
  return (
    <div className="space-y-4 py-3">
      {schema.fields.map((f) => (
        <div key={f.key} className="space-y-1.5">
          <Label className="text-xs font-medium text-slate-700">
            {f.label}{f.required && <span className="text-rose-600 ml-1">*</span>}
          </Label>
          {f.type === "select" ? (
            <Select value={values[f.key] ?? ""} onValueChange={(v) => handleChange(f.key, v)}>
              <SelectTrigger className="h-9 text-sm" data-testid={`pc-field-${f.key}`}><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>
                {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : f.type === "textarea" ? (
            <Textarea rows={2} value={values[f.key] ?? ""} onChange={(e) => handleChange(f.key, e.target.value)} data-testid={`pc-field-${f.key}`} />
          ) : (
            <Input
              type={f.type || "text"}
              step={f.step}
              value={values[f.key] ?? ""}
              onChange={(e) => handleChange(f.key, e.target.value)}
              className="h-9 text-sm"
              data-testid={`pc-field-${f.key}`}
            />
          )}
          {errors[f.key] && <p className="text-[11px] text-rose-600">{errors[f.key]}</p>}
        </div>
      ))}
    </div>
  );
}

// ----- Helpers to summarise panel completeness -------------------

function isFilled(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0 && v.some((x) => typeof x === "string" ? x.trim() : Object.values(x || {}).some(Boolean));
  return true;
}

function panelStats(panel, values, errors) {
  const total = panel.fields.length;
  const filled = panel.fields.filter((f) => isFilled(values[f])).length;
  const hasErrors = Object.keys(errors).some((k) => panel.fields.some((f) => k === f || k.startsWith(`${f}_`)) || (panel.requiredFields || []).some((r) => k === r || k.startsWith(`${r}_`)));
  return { filled, total, hasErrors };
}

// ----- Component ---------------------------------------------------

export default function SubmissionFormDialog({ open, onOpenChange, caseId, caseData, item, onSubmitted }) {
  const kind = item?.kind;
  const full = FULL_SCHEMAS[kind];
  const light = LIGHT_SCHEMAS[kind];
  const schema = full || light;

  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const saveTimer = useRef(null);
  const initialised = useRef(false);

  // Re-seed defaults whenever the dialog opens for a different item.
  useEffect(() => {
    if (open && schema) {
      const defs = full ? full.getDefaults(caseData, item) : schema.defaults(caseData, item);
      setValues(defs);
      setErrors({});
      initialised.current = true;
    } else if (!open) {
      initialised.current = false;
      if (saveTimer.current) clearTimeout(saveTimer.current);
    }
  }, [open, item?.id, item?.kind]);

  // Debounced auto-save: 1.2s after the last keystroke we PATCH the
  // metadata so partial work survives a refresh or accidental close.
  useEffect(() => {
    if (!open || !initialised.current || !item?.id) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        setSavingDraft(true);
        await API.patch(`/cases/${caseId}/post-completion/items/${item.id}`, { metadata: values });
        setLastSavedAt(new Date());
      } catch (err) { /* silent — we surface on submit */ }
      finally { setSavingDraft(false); }
    }, 1200);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [values]);

  const set = (k, val) => {
    setValues((s) => ({ ...s, [k]: val }));
    setErrors((e) => {
      if (!e || !e[k]) return e;
      const n = { ...e };
      delete n[k];
      return n;
    });
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    const tid = toast.loading("Saving draft…");
    try {
      await API.patch(`/cases/${caseId}/post-completion/items/${item.id}`, { metadata: values });
      setLastSavedAt(new Date());
      toast.success("Draft saved", { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed", { id: tid });
    } finally {
      setSavingDraft(false);
    }
  };

  // SDLT-only: show the exact GovTalk XML that would be sent to HMRC.
  // Useful for solicitors to verify the outbound payload before
  // firing at the ChRIS transaction engine.
  const [xmlPreview, setXmlPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const handlePreviewXml = async () => {
    setLoadingPreview(true);
    try {
      // Save current values first so the preview reflects unsaved edits.
      await API.patch(`/cases/${caseId}/post-completion/items/${item.id}`, { metadata: values });
      const { data } = await API.post(
        `/cases/${caseId}/post-completion/items/${item.id}/preview-xml`,
        { metadata: values },
      );
      if (data?.supported === false) {
        toast.info("XML preview is only available for HMRC SDLT submissions.");
        return;
      }
      if (data?.configured === false) {
        toast.error("HMRC SDLT credentials aren't configured on this environment.");
        return;
      }
      setXmlPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSubmit = async () => {
    let errs;
    if (full) {
      errs = full.validate(values);
    } else {
      errs = {};
      schema.fields.forEach((f) => {
        if (f.required && !String(values[f.key] ?? "").trim()) errs[f.key] = "Required";
      });
    }
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error(`Please fix ${Object.keys(errs).length} field${Object.keys(errs).length === 1 ? "" : "s"} before submitting.`);
      return;
    }

    setSubmitting(true);
    const tid = toast.loading(`Submitting ${schema.title.split(" — ")[0]}…`);
    try {
      // Save metadata first so live adapter / cover sheet picks it up
      await API.patch(`/cases/${caseId}/post-completion/items/${item.id}`, { metadata: values });
      const post = (confirmLive) => API.post(
        `/cases/${caseId}/post-completion/items/${item.id}/submit`,
        { metadata: values, confirm_live: !!confirmLive },
      );
      let data;
      try {
        ({ data } = await post(false));
      } catch (err) {
        // 428 = backend is in LIVE mode and wants an explicit go-ahead.
        if (err.response?.status === 428) {
          toast.dismiss(tid);
          const confirmText = kind === "mr01"
            ? "⚠️ LIVE COMPANIES HOUSE FILING\n\n" +
              "This registers a REAL, legally-binding charge (MR01) at Companies House and " +
              "incurs the filing fee — it cannot be undone. Only continue if the company, charge " +
              "particulars and the attached signed deed are correct for a genuine matter.\n\n" +
              "File this LIVE MR01 now?"
            : "⚠️ LIVE HMRC FILING\n\n" +
              "This submits a PERMANENT, legally-binding SDLT return to HMRC for a real " +
              "transaction — it cannot be undone. Only continue if the buyer, seller, " +
              "property and consideration details are correct for a genuine matter.\n\n" +
              "Submit this LIVE SDLT return now?";
          const ok = window.confirm(confirmText);
          if (!ok) { setSubmitting(false); return; }
          ({ data } = await post(true));
        } else {
          throw err;
        }
      }
      onSubmitted(data);
      if (data.status === "rejected") {
        // HTTP 200 but HMRC/Companies House refused it — surface the real
        // reason (now includes the specific ChRIS error + field location)
        // instead of a misleading success toast.
        toast.error(data.message || "The submission was rejected — see the activity log for details.", {
          id: tid, duration: 15000,
        });
        setSubmitting(false);
        return;
      }
      toast.success(`Submitted — ref ${data.submission_ref || "(see activity)"}`, { id: tid });
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed", { id: tid });
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Full (panel) layout -------------------------------------
  const panels = useMemo(() => (full ? full.getPanels().filter((p) => !p.onlyIf || p.onlyIf(values)) : []), [full, values]);

  const totals = useMemo(() => {
    if (!full) return null;
    let totalFields = 0, filledFields = 0;
    panels.forEach((p) => {
      const s = panelStats(p, values, {});
      totalFields += s.total; filledFields += s.filled;
    });
    return { totalFields, filledFields };
  }, [panels, values, full]);

  if (!schema) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="pc-submit-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>{schema.title}</DialogTitle>
          <DialogDescription>{schema.description}</DialogDescription>
        </DialogHeader>

        {full ? (
          <>
            {/* Top status bar — overall progress + last-saved indicator */}
            <div className="flex items-center justify-between text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mt-1">
              <div className="flex items-center gap-2">
                {totals && totals.filledFields >= totals.totalFields ? (
                  <CheckCircle weight="fill" className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Warning weight="fill" className="w-4 h-4 text-amber-500" />
                )}
                <span className="font-medium">
                  {totals?.filledFields ?? 0} of {totals?.totalFields ?? 0} key fields completed
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-slate-500">
                <FloppyDisk weight="bold" className="w-3.5 h-3.5" />
                {savingDraft ? "Saving…" : lastSavedAt ? `Auto-saved ${lastSavedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : "Auto-save on"}
              </div>
            </div>

            <Accordion type="multiple" defaultValue={panels.map((p) => p.id)} className="mt-2">
              {panels.map((p) => {
                const stats = panelStats(p, values, errors);
                const Component = p.Component;
                return (
                  <AccordionItem key={p.id} value={p.id} data-testid={`pc-panel-${p.id}`}>
                    <AccordionTrigger className="text-sm font-semibold text-slate-800 hover:no-underline">
                      <span>{typeof p.label === "function" ? p.label(values) : p.label}</span>
                      <PanelStatusBadge filled={stats.filled} total={stats.total} hasErrors={stats.hasErrors} />
                    </AccordionTrigger>
                    <AccordionContent>
                      <Component v={values} set={set} e={errors} caseId={caseId} caseData={caseData} />
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </>
        ) : (
          <LightForm schema={schema} values={values} setValues={setValues} errors={errors} />
        )}

        <DialogFooter className="border-t border-slate-100 pt-3 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="pc-submit-cancel">
            Cancel
          </Button>
          {full && (
            <Button variant="outline" onClick={handleSaveDraft} disabled={savingDraft || submitting} data-testid="pc-submit-save-draft">
              <FloppyDisk weight="bold" className="w-4 h-4 mr-1" />
              {savingDraft ? "Saving…" : "Save draft"}
            </Button>
          )}
          {kind === "sdlt" && (
            <Button
              variant="outline"
              onClick={handlePreviewXml}
              disabled={loadingPreview || submitting}
              data-testid="pc-sdlt-preview-xml"
              title="Render the GovTalk XML that will be sent to HMRC — without actually sending."
            >
              <Eye weight="bold" className="w-4 h-4 mr-1" />
              {loadingPreview ? "Rendering…" : "Preview XML"}
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-[#1462a0] hover:bg-[#10507f] text-white"
            data-testid="pc-submit-confirm"
          >
            <PaperPlaneTilt weight="bold" className="w-4 h-4 mr-1" />
            {submitting ? "Submitting…" : schema.submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* SDLT XML preview modal */}
      {xmlPreview && (
        <Dialog open={!!xmlPreview} onOpenChange={(v) => !v && setXmlPreview(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="sdlt-xml-preview-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>SDLT GovTalk envelope preview</DialogTitle>
              <DialogDescription>
                This is the exact XML that will be POSTed to HMRC when you submit.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-2 text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mt-1">
              <div><span className="text-slate-500">Environment:</span> <strong className="uppercase">{xmlPreview.env}</strong></div>
              <div><span className="text-slate-500">UTRN:</span> <strong>{xmlPreview.utrn}</strong></div>
              <div className="truncate" title={xmlPreview.irmark}><span className="text-slate-500">IRmark:</span> <strong>{xmlPreview.irmark}</strong></div>
              <div className="col-span-3 truncate"><span className="text-slate-500">Endpoint:</span> <code className="text-[11px]">{xmlPreview.endpoint}</code></div>
            </div>
            <pre className="flex-1 overflow-auto bg-slate-900 text-slate-100 text-[11px] leading-relaxed p-3 rounded-md font-mono mt-2 whitespace-pre-wrap break-all" data-testid="sdlt-xml-body">{xmlPreview.xml}</pre>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { navigator.clipboard.writeText(xmlPreview.xml); toast.success("XML copied to clipboard"); }}
                data-testid="sdlt-xml-copy"
              >
                <Copy weight="bold" className="w-4 h-4 mr-1" /> Copy XML
              </Button>
              <Button onClick={() => setXmlPreview(null)} className="bg-[#1462a0] hover:bg-[#10507f] text-white" data-testid="sdlt-xml-close">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
